import { useRef, useState } from "react";
import type { ProjectDirectory } from "@/api/spaces";
import { PocketRow, PocketSection, ToggleSwitch } from "@/components/ui";
import { useT } from "@/i18n";
import { settingsProjectsMessages } from "@/i18n/messages/settings-projects";
import { SettingsFieldError, SettingsTextField } from "./settings-text-field";
import {
  projectError,
  ProjectPageUnavailable,
  useProjectPage,
} from "./project-page-state";
import {
  useDirectoryEditor,
  useDirectoryText,
} from "./project-directory-fields";

export function GitPage({
  projectId,
  folderId,
}: {
  projectId: string;
  folderId: string;
}) {
  const state = useProjectPage(projectId, folderId);
  if (state.loading || !state.project || !state.folder)
    return (
      <ProjectPageUnavailable loading={state.loading} retry={state.retry} />
    );
  return (
    <GitEditor
      key={`${projectId}:${folderId}`}
      projectId={projectId}
      initial={state.folder}
    />
  );
}
function GitEditor({
  projectId,
  initial,
}: {
  projectId: string;
  initial: ProjectDirectory;
}) {
  const t = useT(settingsProjectsMessages);
  const editor = useDirectoryEditor(projectId, initial);
  const repo = useDirectoryText(projectId, editor, "repo_url");
  const base = useDirectoryText(projectId, editor, "default_base_branch");
  const prefix = useDirectoryText(projectId, editor, "branch_prefix");
  const target = useDirectoryText(projectId, editor, "pr_target_branch");
  const template = useDirectoryText(projectId, editor, "branch_name_template");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const writing = useRef(false);
  async function toggle(value: boolean) {
    if (writing.current) return;
    writing.current = true;
    setSaving(true);
    setError(null);
    try {
      await editor.save({ is_repo: value });
    } catch (failure) {
      setError(projectError(failure, t("updateGitError")));
    } finally {
      writing.current = false;
      setSaving(false);
    }
  }
  return (
    <div data-testid="settings-page-git">
      <PocketSection testId="git-settings">
        <PocketRow
          testId="git-enabled-row"
          title={t("gitRepository")}
          trailing={
            <ToggleSwitch
              checked={editor.directory.is_repo}
              disabled={saving}
              onCheckedChange={(value) => void toggle(value)}
              aria-label={t("gitRepository")}
              data-testid="git-enabled"
            />
          }
        />
        <SettingsFieldError error={error} testId="git-error" />
        <SettingsTextField
          {...repo.inputProps}
          error={repo.error}
          label={t("repositoryUrl")}
          mono
          testId="git-repo-url"
          // i18n-ignore: an example URL
          placeholder="git@github.com:owner/repo.git"
        />
        <SettingsTextField
          {...base.inputProps}
          error={base.error}
          label={t("defaultBaseBranch")}
          mono
          testId="git-base"
          // i18n-ignore: a branch name
          placeholder="main"
        />
        <SettingsTextField
          {...prefix.inputProps}
          error={prefix.error}
          label={t("branchPrefix")}
          mono
          testId="git-prefix"
          // i18n-ignore: an example prefix
          placeholder="coder/"
        />
        <SettingsTextField
          {...target.inputProps}
          error={target.error}
          label={t("prTarget")}
          mono
          testId="git-target"
          placeholder={t("sameAsBase")}
        />
        <SettingsTextField
          {...template.inputProps}
          error={template.error}
          label={t("branchNameTemplate")}
          mono
          testId="git-template"
          // i18n-ignore: template variables
          placeholder="{prefix}{task_id}-{slug}"
        />
      </PocketSection>
    </div>
  );
}
